import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import {
  catchError, tap,
} from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { environment } from '../../../../environments/environment';
import { getErrorMessage } from '../../helpers';
import { isString } from 'lodash-es';
import { IMPERSONATED_EMAIL_KEY } from '../../../core/config';

export interface BaseApiParamsModel {
  [param: string]: boolean | number | string | string[];
}

export interface BaseApiHandleOptions {
  additionalHeaders?: HttpHeaders;
  excludeImpersonate?: boolean;
  params?: HttpParams | BaseApiParamsModel;
  showError?: boolean;
  showSuccess?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class BaseApiService {
  private readonly defaultConfig: BaseApiHandleOptions = {
    excludeImpersonate: false,
    showError: false,
    showSuccess: false,
  };

  constructor(
    private readonly httpClient: HttpClient,
    private readonly toastrService: ToastrService,
  ) {
  }

  get<T>(
      url: string,
      options: BaseApiHandleOptions = {},
      additionalOptions: { responseType?: 'json' | 'text', observe?: 'body' | 'response' } = {},
  ): Observable<T> {
    const { params, excludeImpersonate, showError, showSuccess, additionalHeaders } = {
      ...this.defaultConfig,
      ...options,
    }
    // @ts-ignore
    return this.execute(
      // @ts-ignore
      this.httpClient.get<T>(`${environment.baseUrl}/${url}`, {
        ...(params ? { params } : {}),
        headers: this.getHeaders(excludeImpersonate, additionalHeaders),
        ...additionalOptions,
      }), showSuccess, showError,
    );
  }

  post<T>(
    url: string,
    body: object | null | Array<string> | string = null,
    options: BaseApiHandleOptions = {},
    additionalOptions: { responseType?: 'json' | 'text', observe?: 'body' | 'response' } = {},
  ): Observable<T> {
    const { params, excludeImpersonate, showError, showSuccess } = {
      ...this.defaultConfig,
      ...options,
    }
    // @ts-ignore
    return this.execute(
      // @ts-ignore
      this.httpClient.post<T>(`${environment.baseUrl}/${url}`, body, {
        ...(params ? { params } : {}),
        headers: this.getHeaders(excludeImpersonate),
        ...additionalOptions,
      }),
      showSuccess,
      showError,
    );
  }

  delete<T>(
    url: string,
    options: BaseApiHandleOptions = {},
    body: object | null | Array<string> | string = null,
  ): Observable<T> {
    const { params, excludeImpersonate, showError, showSuccess } = {
      ...this.defaultConfig,
      ...options,
    }
    return this.execute(
      this.httpClient.delete<T>(`${environment.baseUrl}/${url}`, {
        ...(params ? { params } : {}),
        headers: this.getHeaders(excludeImpersonate),
        body,
      }),
      showSuccess,
      showError,
    );
  }

  put<T>(
    url: string,
    body: object | null | Array<string> | string = null,
    options: BaseApiHandleOptions = {},
    additionalOptions: { responseType?: 'json' | 'text', observe?: 'body' | 'response' } = {},
  ): Observable<T> {
    const { params, excludeImpersonate, showError, showSuccess } = {
      ...this.defaultConfig,
      ...options,
    };

    // @ts-ignore
    return this.execute(
      // @ts-ignore
      this.httpClient.put<T>(`${environment.baseUrl}/${url}`, body, {
        ...(params ? { params } : {}),
        headers: this.getHeaders(excludeImpersonate),
        ...additionalOptions,
      }),
      showSuccess,
      showError,
    );
  }

  patch<T>(
    url: string,
    body: object | null | Array<string> | string = null,
    options: BaseApiHandleOptions = {},
    additionalOptions: { responseType?: 'json' | 'text', observe?: 'body' | 'response' } = {},
  ): Observable<T> {
    const { params, excludeImpersonate, showError, showSuccess } = {
      ...this.defaultConfig,
      ...options,
    };

    // @ts-ignore
    return this.execute(
      // @ts-ignore
      this.httpClient.patch<T>(`${environment.baseUrl}/${url}`, body, {
        ...(params ? { params } : {}),
        headers: this.getHeaders(excludeImpersonate),
        ...additionalOptions,
      }),
      showSuccess,
      showError,
    );
  }

  private execute<T>(
    call: Observable<T>,
    showSuccess: boolean = true,
    showError: boolean = true,
  ): Observable<T> {
    return call
      .pipe(
        tap((r: T) => this.handleSuccess(showSuccess, (r as any)?.message || (r as any)?.response)),
        catchError((error: HttpErrorResponse) => this.handleError(showError, error)),
      );
  }

  private handleError(showError: boolean, errorResponse: HttpErrorResponse): Observable<never> {
    if (showError) {
      const message: string = getErrorMessage(errorResponse);
      this.toastrService.error(message);
    }
    return throwError(errorResponse);
  }

  private handleSuccess(showSuccess: boolean, message: string): void {
    if (showSuccess) {
      this.toastrService.success(isString(message) ? message : 'Success');
    }
  }

  private getHeaders(excludeImpersonate: boolean = false, additionalHeaders?: HttpHeaders): HttpHeaders {
    const impersonatedEmail: string = sessionStorage.getItem(IMPERSONATED_EMAIL_KEY);
    return new HttpHeaders({
      ...(!excludeImpersonate && impersonatedEmail ? { 'X-Impersonate-User': impersonatedEmail } : {}),
      ...(additionalHeaders ? additionalHeaders.keys().reduce((acc: {
        [name: string]: string | number | (string | number)[];
      }, key: string) => ({
        ...acc,
        [key]: additionalHeaders.get(key),
      }), {}) : {}),
    });
  }
}
